import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import * as Objects from '@core/objects';

import { VersionsViewService } from '@app/services/app/versions.service';
import { AppService } from '@app/services/app/app.service';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { FilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { AnnotationsService } from '@core/services/annotations/annotations.service';
import { ShareVersionFeedback, VersionFeedback } from '@core/models/version-feedback';
import { VersionShare, VersionShareEntryDetail, VersionShareOptionName, getVersionShareOption, getVersionShareFiles, isVersionShareOpen, isFeedbackVersionShare } from '@core/models/version-share.model';
import { InterfaceListenerService } from '@core/services/interface-listener/interface-listener.service';

@Component({
    selector: 'versions-details-content-preview-file-main',
    templateUrl: './preview-file-main.component.html',
    styleUrls: ['./preview-file-main.component.scss']
})
export class VersionsDetailsContentPreviewFileMainComponent implements OnInit{

    protected readonly PLUGIN_FEEDBACK = 'share_feedback';
    protected readonly VERSION_SHARE_FEEDBACK_CONTEXT = 'version-share-feedback';

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_NOT_ALLOWED = 2;

    @Input() version: Objects.Version = null;
    @Input() file: Objects.File = null;
    @Input() menu: Array<ThreeDotsMenuItem> = [];

    @Output() menuClick = new EventEmitter<FilesListItemMenuClick>();
    @Output() actionDownloadClick = new EventEmitter<Objects.AmsFile>();
    
    public status: number = this.STATUS_LOADING;
    // for shared files to get/create feedback notes
    public notesUrlPrefix: string = '';
    public annotationsUrlPrefix: string = '';
    public annotationsSaveUrlPrefix: string = '';
    public canCreateNotes: boolean;
    public loadingNotesAccess: boolean = true;

    public pluginsAccess: Array<string> = [];
    public accessFeedbacks: boolean = null;
    // @deprecated oct-2026: legacy share feedback url call response, replaced by myShare
    // public share: any = null;
    // oct-2026: current user's open approval/feedback entry on this version (null when not a recipient)
    public myShare: VersionShareEntryDetail = null;
    public sharedFileIds: Array<number> = null;
    public context: string = 'default';
    public notesCounter:  Array<Objects.AnnotationsCounter>;
    public notesCounterUrlPrefix: string = '';

    protected shareVersionSubscriptor: Subscription;
    protected appSubscriptor: Subscription;
    protected feedbacksSubscriptor: Subscription;
    protected iListenerSubs: Subscription;

    // @deprecated oct-2026: legacy share feedback bubbles url call response, replaced by feedbackShares and myShare
    // protected feedbacks: Array<VersionFeedback> = null;
    // protected userFeedback: VersionFeedback = null;
    // oct-2026: approval/feedback entries of every recipient on this version (null until loaded)
    protected feedbackShares: Array<VersionShare> = null;
    
    protected user: Objects.User = null;

    public breadcrumbs: any = [
        {
            title: "",
            link: ['']
        }
    ];

    // extra menu item depending on notes access.
    public menuPdfWithComments: ThreeDotsMenuItem = {
        title: "Download with comments",
        key: "download-comments",
        icon: "download_comments",
        order: 4
    };


    constructor (
        private viewVersionSrv: VersionsViewService,
        private annotationsSrv: AnnotationsService,
        private iListenerSrv: InterfaceListenerService,
        private appSrv: AppService){}

    ngOnInit(): void {
        // @deprecated oct-2026: legacy share feedback url call, replaced by selectMyVersionShares
        // this.shareVersionSubscriptor = this.viewVersionSrv.selectShareFeedback().subscribe((shareVersionFeedback: ShareVersionFeedback) => {
        //     // here comes the shareVersionFeedback with properties:
        //     if(shareVersionFeedback != null){
        //         this.share = shareVersionFeedback.share;
        //         if( this.share.entry &&
        //             this.share.entry.share &&
        //             this.share.entry.share.properties )
        //         {
        //             let filesProp = this.share.entry.share.properties.filter((p:any) => p.key == 'files');
        //             // to know which files of the version were shared
        //             this.sharedFileIds = filesProp.length > 0 ? (filesProp[0]).value : [];
        //             this.updateUserNotesAccess();
        //         }
        //     }
        // });

        this.shareVersionSubscriptor = this.viewVersionSrv.selectMyVersionShares().subscribe((myShares: Array<VersionShareEntryDetail>) => {
            // null until loaded: wait for it before deciding the notes access
            if(myShares == null)
                return;
            this.myShare = myShares.find((ms: VersionShareEntryDetail) => isFeedbackVersionShare(ms)) ?? null;
            // to know which files of the version were shared
            this.sharedFileIds = getVersionShareFiles(this.myShare);
            this.updateUserNotesAccess();
        });

        this.appSubscriptor = this.appSrv.getSession().subscribe( (session: any) => {
            this.pluginsAccess = session.options.plugins;
            this.user = session.current.user;
            
            this.accessFeedbacks = this.checkPluginAccess(this.PLUGIN_FEEDBACK);
            if(this.accessFeedbacks){
                // this.viewVersionSrv.loadShareFeedback(this.user);
                this.initFeedbacks();
            }else
                this.initView();
        });

        // to know when note is saved or removed.
        this.iListenerSubs = this.iListenerSrv.getFileAnnotationsSaved().subscribe((file: Objects.File) => {
            // Handle the version feedback 'onDone' event here to reload the notes counter.
            if(this.version != null && this.file!=null && file && file.id == this.file.id){
                this.loadAnnotationsCounter()
            }
        });
    }

    ngOnDestroy() {
        this.shareVersionSubscriptor.unsubscribe();
        this.appSubscriptor.unsubscribe();
        this.iListenerSubs.unsubscribe();
        if(this.feedbacksSubscriptor)
            this.feedbacksSubscriptor.unsubscribe();
    }

    onMenuClick(menu: FilesListItemMenuClick) {
        this.menuClick.emit(menu);
    }

    onActionDownloadClick(file: Objects.AmsFile){
        this.actionDownloadClick.emit(file);
    }

    protected initView(){
        if(this.version == null || this.loadingNotesAccess)
            return;

        
        if(this.myShare == null && this.feedbackShares != null && this.feedbackShares.length == 0){
            // no feedbacks for this version --> preview simple
            this.context = 'version';
            this.status = this.STATUS_DONE;
            return;
        } else {
            // dec-2024: when context is not 'version' (no notes at all) 
            // it load counters to check menu item for downlad comments
            if(this.version != null && this.file != null){
                // prefix to get files notes in app
                this.notesCounterUrlPrefix = "/versions/"+this.version.id+"/feedbacks";
                this.loadAnnotationsCounter();
            } 
        }
        // in cases when user has no share but there are feedabcks requested or user has share without approvals
        if( (this.myShare == null && this.feedbackShares != null && this.feedbackShares.length > 0) || this.myShare != null ){
            // have share and feedbacks for this version --> preview with notes
            this.status = this.STATUS_DONE;
            return;
        }
    }


    // @deprecated oct-2026: legacy share feedback bubbles url call, the open status now comes from myShare
    // protected loadMyFeedback(){
    //     if (!this.accessFeedbacks || this.user == null){
    //         return;
    //     }

    //     // call /web/app/p/feedback/versions/{vid}/users/{userId}
    //     if (this.version != null && this.feedbacks !=null){
    //         this.userFeedback = null;
    //         let myFBs = this.feedbacks.filter((f: VersionFeedback) => f.user.id == this.user.id );
    //         this.userFeedback = myFBs.length > 0 ? myFBs[0] : null;
    //         // once we have the current user feedbac (or not) need to update the access
    //         this.updateUserNotesAccess();
    //     } else {
    //         this.initView();
    //     }
    // }

    protected checkPluginAccess(plugin: string){
        return this.pluginsAccess.some((pa) => pa == plugin);
    }

    protected updateUserNotesAccess() : void {
        if(this.user != null && this.myShare != null && this.file != null){
            this.canCreateNotes = (getVersionShareOption(this.myShare, VersionShareOptionName.GET_COMMENTS) &&
                                this.myShare.entry.user.id == this.user.id &&
                                isVersionShareOpen(this.myShare)
                                && this.sharedFileIds.some((sfid) => sfid == this.file.id)
                            );   
            this.loadingNotesAccess = false;                           
            this.annotationsSaveUrlPrefix = "/web/share/feedback/"+this.myShare.entry.token+"/versions/"+this.version.id;
            // context is only 'version-share-feedback' when we need to save notes in /web/share, in other cases use default /web/app always
            this.context = this.VERSION_SHARE_FEEDBACK_CONTEXT;
        } 
        if(this.myShare == null){ // avoid notes access when user has no feedback here
            this.canCreateNotes = false;
            this.loadingNotesAccess = false;  
        }
        this.annotationsUrlPrefix = "/versions/"+this.version.id+"/feedbacks";
        this.notesUrlPrefix = "/p/feedback/versions/"+this.version.id;
        this.initView(); 
    }

    protected loadAnnotationsCounter() {
        this.annotationsSrv.getCounter(this.notesCounterUrlPrefix).subscribe({
            next: (counter: Objects.AnnotationsCounter[]) => {
                this.notesCounter = counter;
                // check if file has annotations
                const hasAnnotations = this.notesCounter.find(note => note.file?.id === this.file.id)?.annotations?.count > 0;
                this.updateMenu(this.menuPdfWithComments, hasAnnotations)
                // this.loadingCounter = false;
                this.status = this.STATUS_DONE;
            },
            error: (error: HttpErrorResponse) => {
                console.debug(" ERROR getting message notes counter:",error);
                this.notesCounter = [];
                // this.loadingCounter = false;
                this.status = this.STATUS_DONE;
            }
        });
    }

    protected initFeedbacks(){
        // @deprecated oct-2026: legacy share feedback bubbles url call, replaced by selectVersionShares
        // this.feedbacksSubscriptor = this.viewVersionSrv.selectFeedbacks().subscribe((feedbacks: Array<VersionFeedback>) => {
        //     this.feedbacks = feedbacks;
        //     this.loadMyFeedback();
        // })
        this.feedbacksSubscriptor = this.viewVersionSrv.selectVersionShares().subscribe((allShares: Array<VersionShare>) => {
            if(allShares == null)
                return;
            this.feedbackShares = allShares.filter((vs: VersionShare) => isFeedbackVersionShare(vs));
            this.initView();
        })
    }

    // this function check either remove or add item. based on subscribed events
    protected updateMenu(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.menu.push(item);
        }
    }

}
